import { stringifyGraph } from '@/clients/http'
import { BlogModel } from '@/http/models/blog'
import { MediaModel } from '@/http/models/media/Media'
import { BLOG_GRAPH, BLOG_STATUS_PUBLISHED, type BlogStatus } from '@iws/theme-schema/blog'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiErrorMessage, fetchBlogBody, uploadBlogBody } from '@/lib/blog'

import type { BlogPost } from '@/types/blog'

const BLOG_KEY = 'blog'

export interface BlogListQuery {
  page: number
  length: number
  search: string
  status: BlogStatus | 'all' | 'trash'
}

export const useBlogPosts = ({ page, length, search, status }: BlogListQuery) =>
  useQuery({
    queryKey: [BLOG_KEY, 'list', page, length, search, status],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await BlogModel.getList({
        query: {
          page,
          length,
          search: search || undefined,
          status: status === 'all' || status === 'trash' ? undefined : status,
          deleted: status === 'trash' ? 1 : undefined,
          graph_enc: stringifyGraph(BLOG_GRAPH),
        },
      })
      if (!res.success || !res.data) throw new Error(apiErrorMessage(res, 'Could not load blog posts'))
      return res.data
    },
  })

export const useBlogPost = (id: string | undefined) =>
  useQuery({
    queryKey: [BLOG_KEY, 'post', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await BlogModel.getById({ params: { id: id! }, query: { graph_enc: stringifyGraph(BLOG_GRAPH) } })
      if (!res.success || !res.data) throw new Error(apiErrorMessage(res, 'Could not load the post'))
      return res.data.result
    },
  })

/** The post body HTML, read from its media file. */
/** A post's body; cached per body file, which changes on every save. */
export const useBlogBody = (id: string | undefined, contentId: string | null | undefined) =>
  useQuery({
    queryKey: [BLOG_KEY, 'body', contentId],
    enabled: Boolean(id && contentId),
    staleTime: Infinity,
    queryFn: () => fetchBlogBody(id!),
  })

export interface SaveBlogInput {
  id?: string
  title: string
  html: string
  bannerId: string | null
  readDuration: string
  /** Empty: keep the current address (or, for a new post, let the backend make one from the title). */
  slug: string
  excerpt: string
  category: string
  tags: string[]
  authorName: string
  seoTitle: string
  seoDescription: string
  /** ISO time to go live (a future one schedules the post); null keeps the current time, or now on first publish. */
  publishAt: string | null
  status: BlogStatus
  /** The body file the post pointed at before this save; removed once the new one is saved. */
  previousContentId?: string | null
}

export interface SaveBlogResult {
  post: BlogPost
  /** The previous body file could not be removed (it stays in the media library). */
  staleBody: boolean
}

/** Uploads the body as a new media file, then creates or updates the post pointing at it and removes the old body file. */
export const useSaveBlogPost = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id, title, html, bannerId, readDuration, slug, excerpt, category, tags, authorName, seoTitle, seoDescription, publishAt, status, previousContentId,
    }: SaveBlogInput): Promise<SaveBlogResult> => {
      const body = {
        title: title.trim(),
        content_id: await uploadBlogBody(html, title),
        banner_id: bannerId,
        read_duration: readDuration.trim() || undefined,
        slug: slug.trim() || undefined,
        excerpt: excerpt.trim(),
        category: category.trim(),
        tags,
        author_name: authorName.trim(),
        seo_title: seoTitle.trim(),
        seo_description: seoDescription.trim(),
        ...(publishAt && status === BLOG_STATUS_PUBLISHED ? { published_at: publishAt } : {}),
        status,
      }
      const res = id ? await BlogModel.update({ params: { id }, body }) : await BlogModel.create({ body })
      if (!res.success || !res.data) throw new Error(apiErrorMessage(res, 'Could not save the post'))
      let staleBody = false
      if (previousContentId && previousContentId !== body.content_id) {
        const removed = await MediaModel.deleteMedia({ id: previousContentId }).catch(() => null)
        staleBody = !removed?.success
      }
      return { post: res.data.result, staleBody }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [BLOG_KEY] }),
  })
}

export const useDeleteBlogPost = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await BlogModel.delete({ params: { id } })
      if (!res.success) throw new Error(apiErrorMessage(res, 'Could not delete the post'))
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [BLOG_KEY] }),
  })
}

export const useRestoreBlogPost = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await BlogModel.restore({ params: { id } })
      if (!res.success) throw new Error(apiErrorMessage(res, 'Could not restore the post'))
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [BLOG_KEY] }),
  })
}

/**
 * Deletes a trashed post for good, then its body file. Returns false when the body file could not be removed
 * (it stays in the media library); the post itself is gone either way.
 */
export const usePermanentDeleteBlogPost = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<boolean> => {
      const res = await BlogModel.permanentDelete({ params: { id } })
      if (!res.success) throw new Error(apiErrorMessage(res, 'Could not delete the post'))
      const bodyId = res.data?.result?.content_id
      if (!bodyId) return true
      const removed = await MediaModel.deleteMedia({ id: bodyId }).catch(() => null)
      return Boolean(removed?.success)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [BLOG_KEY] }),
  })
}
